import { fetchQuery, graphql } from 'react-relay';
import { type IEnvironment } from 'relay-runtime';

import { request } from '@atlassian/twg-common/request';

import { type utils_twg_microsite_CurrentUser_Query } from './__generated__/utils_twg_microsite_CurrentUser_Query.graphql';
import { type LoggedInUser } from './types';

export type FetchUserProfileResult = {
	identity: LoggedInUser | null;
	error: boolean;
	loginRedirectFailed: boolean;
};

/**
 * `reason` tells the two failure shapes apart. The gateway answering with no
 * user (`anonymous`) is a normal visitor state. The query throwing
 * (`request-failed`) is not a verdict on the session: the shared Relay network
 * layer drops the HTTP status, so a refused cookie and a server error look the
 * same here and need `probeSessionStatus` to tell them apart.
 */
export type QueryUserProfileResponse =
	| {
			success: true;
			identity: LoggedInUser;
	  }
	| {
			success: false;
			reason: 'anonymous';
			error: string;
	  }
	| {
			success: false;
			reason: 'request-failed';
			error: unknown;
	  };

export async function queryUserProfile(
	environment: IEnvironment,
): Promise<QueryUserProfileResponse> {
	try {
		const data = await fetchQuery<utils_twg_microsite_CurrentUser_Query>(
			environment,
			graphql`
				query utils_twg_microsite_CurrentUser_Query {
					me {
						user {
							... on AtlassianAccountUser {
								id
								accountId
								name
								nickname
								picture
								email
								orgId
								createdAt
								extendedProfile {
									jobTitle
									organization
								}
							}
						}
					}
				}
			`,
			{},
		).toPromise();

		const id = data?.me?.user?.id ?? '';
		const accountId = data?.me?.user?.accountId ?? '';
		const email = data?.me?.user?.email ?? '';
		const name = data?.me?.user?.name ?? '';
		const nickname = data?.me?.user?.nickname ?? '';
		const picture = data?.me?.user?.picture ?? '';
		const orgId = data?.me?.user?.orgId ?? '';
		const createdAt = data?.me?.user?.createdAt ?? '';
		if ([id, accountId].some((x) => x.length === 0)) {
			return { success: false, reason: 'anonymous', error: 'id or accountId is missing' };
		}
		const normalizedEmail = email.toLowerCase();
		const isAtlassianEmployee = normalizedEmail.endsWith(ATLASSIAN_EMAIL_DOMAIN);
		const extendedProfile = data?.me?.user?.extendedProfile;
		return {
			success: true,
			identity: {
				id,
				accountId,
				email,
				name,
				nickname,
				picture,
				staffId: getStaffIdFromEmail(email),
				isAtlassianEmployee,
				organisation: extendedProfile?.organization ?? '',
				jobTitle: extendedProfile?.jobTitle ?? '',
				orgId,
				createdAt,
			},
		};
	} catch (e) {
		return { success: false, reason: 'request-failed', error: e };
	}
}

export type SessionProbeResult = 'authenticated' | 'anonymous' | 'rejected' | 'unavailable';

type SessionProbeResponse = {
	data?: {
		me?: {
			user?: {
				accountId?: string | null;
			} | null;
		} | null;
	} | null;
};

const SESSION_PROBE_QUERY = 'query twg_microsite_SessionProbe { me { user { accountId } } }';

/**
 * Asks the same gateway endpoint the profile query uses, through the plain
 * request helper so the HTTP status survives. Only called after
 * `queryUserProfile` threw, which is rare: an expired cookie or an outage.
 *
 * 401 is the gateway refusing the cookie. Anything else that is not a 200 is
 * reported as unavailable, including 403, which is an authorization decision
 * about a signed-in user rather than an expired session.
 */
export const probeSessionStatus = async (): Promise<SessionProbeResult> => {
	const response = await request<SessionProbeResponse>('POST', '/gateway/api/graphql', {
		query: SESSION_PROBE_QUERY,
		variables: {},
	});

	if (response.success) {
		const accountId = response.data.data?.me?.user?.accountId ?? '';
		return accountId === '' ? 'anonymous' : 'authenticated';
	}

	return response.statusCode === 401 ? 'rejected' : 'unavailable';
};

const ATLASSIAN_EMAIL_DOMAIN = '@atlassian.com';

const getStaffIdFromEmail = (email: string): string => {
	return email.split('@').at(0) ?? '';
};
